import { useState, useEffect } from 'react';
import { apiGet } from './apiClient';

export interface TopTrader {
    account_id: string;
    realized_pnl: number;
    unrealized_pnl: number;
    total_pnl: number;
    open_position: number;
    latest_price: number;
    total_volume: number;
    total_buy_quote: number;
    total_sell_quote: number;
    avg_buy_price: number;
    avg_sell_price: number;
    buy_count: number;
    sell_count: number;
    total_count: number;
}

export type TraderSort = 'best' | 'worst';

interface TopTradersResponse {
    market_id: string;
    traders: TopTrader[];
}

export function useTopTraders(
    conditionId: string | undefined,
    startDate: Date | null,
    endDate: Date | null,
    sort: TraderSort = 'best',
    limit: number = 20,
) {
    const [data, setData] = useState<TopTrader[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!conditionId || !startDate || !endDate) return;

        let cancelled = false;
        const controller = new AbortController();
        setLoading(true);
        setError(null);

        const params = new URLSearchParams({
            from_ts: startDate.toISOString(),
            to_ts: endDate.toISOString(),
            sort,
            limit: String(limit),
        });

        apiGet<TopTradersResponse>(
            `/api/trades/top-traders/${encodeURIComponent(conditionId)}?${params}`,
            controller.signal
        )
            .then((res) => {
                if (!cancelled) setData(res.traders);
            })
            .catch((err) => {
                if (cancelled || err?.name === 'AbortError') return;
                setError(err.message ?? 'Failed to load traders');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [conditionId, startDate?.getTime(), endDate?.getTime(), sort, limit]);

    return { data, loading, error };
}
